/**
 * `useMeHoursVsContract` — TanStack Query hook for the caller's own
 * hours-vs-contract row (`GET /api/account/me/hours_vs_contract`).
 *
 * Backs both the `/me/hours` detail page (`web/src/routes/me/MeHours.tsx`)
 * and the `<HoursPillSection/>` panel pill (#212). Sharing the hook
 * means the pill and the detail page share cache — opening the panel
 * doesn't double-fetch when the worker then clicks through to `/me/hours`.
 *
 * Cache-key shape matches the established pattern (`useMySchedule`,
 * `useWorkspaces`): `["me-hours-vs-contract", accountId]`. `staleTime`
 * = 60s aligned with siblings.
 *
 * Types mirror `MeHoursVsContractResponse` from
 * `backend/src/api/routes/account.py`. Inline types (instead of
 * pulling from `schema.gen.ts`) match the precedent set by
 * `useMySchedule` and the original inline definition in `MeHours.tsx`
 * — the response is small enough to keep self-contained.
 *
 * Status nouns + tones are exported here (not just in `MeHours.tsx`)
 * because both consumers read the same row and must label it the same
 * way — that's the trust handshake per
 * `docs/design/lead-hours-vs-contract-plan.md` §3.6.
 */
import { useQuery } from "@tanstack/react-query";

import { useAuth } from "../lib/AuthContext";
import { type ApiError, apiFetch } from "./client";

export type MeHoursStatus = "above_contract" | "on_contract" | "below_contract" | "no_cap_set";

export interface MeHoursVsContractRow {
  contract_hours_per_week: number | null;
  effective_cap_hours_per_week: number | null;
  scheduled_hours_per_cycle: number;
  scheduled_hours_per_week_avg: number;
  delta_per_week_hrs: number | null;
  status: MeHoursStatus;
}

export interface MeHoursVsContractScope {
  team_id: string;
  team_name: string;
  cycle_id: string;
  cycle_name: string;
  cycle_horizon_weeks: number;
  n_days: number;
}

// Issue #155 finisher — multi-cycle history series. Each point
// mirrors the focal `MeHoursVsContractRow` shape plus cycle identity
// for the chart's x-axis. Server returns up to 7 points (current
// cycle + prior 6 published cycles), newest first; the chart
// reverses for left-to-right time order.
export interface MeHoursVsContractCyclePoint {
  cycle_id: string;
  cycle_name: string;
  horizon_start: string;
  cycle_horizon_weeks: number;
  contract_hours_per_week: number | null;
  effective_cap_hours_per_week: number | null;
  scheduled_hours_per_cycle: number;
  scheduled_hours_per_week_avg: number;
  delta_per_week_hrs: number | null;
  status: MeHoursStatus;
}

// Issue #291 / #292 — workload-axis block. Gated server-side by the
// per-team `workload_axis` component flag. The server omits the field
// (or returns `null`) when the component is off — older SPA builds
// that don't read the field keep working byte-equivalent.
//
// The sibling `absorbed_load` block was removed from the SPA in #345
// per the 2026-05-12 founder direction (the per-cycle absorption tally
// + team-average comparison + "next cycle's solver will rebalance"
// promise contradicted the prosocial-norm guard). The backend
// continues to emit the field for back-compat with #290's solver
// output; the SPA simply stops reading it.

export interface MeStationHoursEntry {
  station_name: string;
  raw_hours: number;
  adjusted_hours: number;
  weight: number;
}

export interface MeWorkloadAxisBlock {
  workload_adjusted_total: number;
  by_station?: MeStationHoursEntry[];
}

// Issue #381 — leave-proportional workload-share offset block. The
// solver applies the offset at solve time (see
// `scheduler.model._apply_solver_weights_registry`'s `workload_share`
// branch); this block surfaces the formula's inputs (leave-day count,
// cycle length, share ratio) so the SPA renders "your share this
// cycle is reduced for your leave days" without recomputing. The card
// is rendered only when `leave_days_in_cycle > 0`.
export interface MeWorkloadShareOffsetBlock {
  leave_days_in_cycle: number;
  cycle_total_days: number;
  share_ratio: number;
}

export interface MeHoursVsContractResponse {
  state: "ready" | "no_membership" | "no_data" | "no_cap_set";
  generated_at: string;
  scope: MeHoursVsContractScope | null;
  row: MeHoursVsContractRow | null;
  // Optional in the type for older builds that haven't seen the field;
  // server always populates (empty list on the empty-state paths).
  history?: MeHoursVsContractCyclePoint[];
  // #291: per-team workload block. Omitted (or null) when the
  // component is off for the caller's team — additive on the wire.
  workload_axis?: MeWorkloadAxisBlock | null;
  // #381: leave-offset workload-share block. Always present on the
  // ready paths; omitted on the empty-state paths. Additive on the
  // wire — older SPA builds that don't read the field keep working.
  workload_share_offset?: MeWorkloadShareOffsetBlock | null;
}

// Status-noun copy + tone palette. Lead-side `/cycles/:id/hours` and
// worker-side `/me/hours` + the `<HoursPillSection/>` panel pill all
// label the same row identically — that's the load-bearing trust
// handshake (BP-1) per `feedback_brand_pillars.md`. Threshold colouring
// per `lead-hours-vs-contract-plan.md` §3.8: above = warning amber,
// on = success green, below + no-cap = neutral grey. Never red —
// under-utilisation is not failure, an unset cap is a workflow gap not
// the worker's fault.
export const ME_HOURS_STATUS_LABEL: Record<MeHoursStatus, string> = {
  above_contract: "Above contract",
  on_contract: "On contract",
  below_contract: "Below contract",
  no_cap_set: "No max set",
};

export const ME_HOURS_STATUS_TONE: Record<MeHoursStatus, "warning" | "success" | "neutral"> = {
  above_contract: "warning",
  on_contract: "success",
  below_contract: "neutral",
  no_cap_set: "neutral",
};

const STALE_TIME_MS = 60_000;

export function meHoursVsContractKey(accountId: string | null) {
  return ["me-hours-vs-contract", accountId ?? ""] as const;
}

export function useMeHoursVsContract() {
  const { accountId } = useAuth();
  return useQuery<MeHoursVsContractResponse, ApiError | Error>({
    queryKey: meHoursVsContractKey(accountId),
    enabled: Boolean(accountId),
    staleTime: STALE_TIME_MS,
    retry: false,
    queryFn: () => apiFetch<MeHoursVsContractResponse>("/api/account/me/hours_vs_contract"),
  });
}
